import { useEffect, useRef, type CSSProperties } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

export function PetParallaxCutout({
  src,
  srcSet,
  sizes,
  alt = '',
  className = '',
  width,
  height,
  scrollProgress = 0,
  fetchPriority,
  loading,
}: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const stage = stageRef.current;

    if (!stage || prefersReducedMotion) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const rect = stage.getBoundingClientRect();
      const x = (event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5;
      const y = (event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5;
      stage.style.setProperty('--pet-x', x.toFixed(3));
      stage.style.setProperty('--pet-y', y.toFixed(3));
    };

    const handlePointerLeave = () => {
      stage.style.setProperty('--pet-x', '0');
      stage.style.setProperty('--pet-y', '0');
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [prefersReducedMotion]);

  const rotate = -3 + scrollProgress * 8;
  const scale = 1 + scrollProgress * 0.12;
  const lift = Math.sin(scrollProgress * Math.PI) * 22;

  return (
    <div
      ref={stageRef}
      className={`pointer-events-none absolute inset-0 ${className}`}
      style={{ '--pet-x': '0', '--pet-y': '0' } as CSSProperties}
    >
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt=""
        aria-hidden="true"
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        className="absolute inset-0 m-auto h-[92%] w-auto object-contain opacity-30 blur-[1.5px]"
        style={{
          transform: prefersReducedMotion
            ? 'translate3d(0, 14px, 0) scale(1.05)'
            : `translate3d(calc(var(--pet-x) * -18px), calc(var(--pet-y) * 12px + ${lift * 0.35 + 16}px), 0) rotate(${rotate * 0.55}deg) scale(${scale * 1.04})`,
        }}
      />
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        className="absolute inset-0 m-auto h-[88%] w-auto object-contain drop-shadow-[0_28px_48px_rgba(0,0,0,0.38)] will-change-transform"
        style={{
          transform: prefersReducedMotion
            ? undefined
            : `translate3d(calc(var(--pet-x) * 32px), calc(var(--pet-y) * -20px + ${lift}px), 0) rotate(${rotate}deg) scale(${scale})`,
        }}
      />
    </div>
  );
}

interface Props {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  scrollProgress?: number;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'eager' | 'lazy';
}
